import { useState, useCallback, useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import ActivityTracker, { WorkoutType, WorkoutConfirmationData } from '../plugins/ActivityTrackerPlugin'
import { ActivityEntry } from '../services/api.types'
import { activityUploadService, UploadEvent, UploadStats } from '../services/ActivityUploadService'
import { globalActivityTrackerService } from '../services/GlobalActivityTrackerService'
import { getUserCompanies } from '../common/auth/authSelectors'
import { hasFeatureFlag } from '../common/helpers/featureFlags'

// Hook return type
interface UseActivityTrackerReturn {
  // Show the workout tracker interface
  showWorkoutTracker: () => Promise<void>
  // Start a workout with a specific activity type
  startWorkout: (activityType: WorkoutType) => Promise<void>
  // Hide the currently displayed ActivityTracker
  hideActivityTracker: () => Promise<void>
  // Check if ActivityTracker is currently visible
  isActivityTrackerVisible: () => Promise<boolean>
  // Confirm and save a workout with updated data
  confirmWorkout: (title: string, type: WorkoutType) => Promise<void>
  // Discard the current workout
  discardWorkout: () => Promise<void>
  // Current visibility state
  isVisible: boolean
  // Loading state for operations
  isLoading: boolean
  // Error state
  error: string | null
  // Success message when activity is uploaded
  successMessage: string | null
  // Upload statistics and offline support
  uploadStats: UploadStats
  // Workout confirmation data when confirmation is required
  confirmationData: WorkoutConfirmationData | null
  // Whether workout confirmation is currently required
  isConfirmationRequired: boolean
  // Manually retry offline uploads
  retryOfflineUploads: () => Promise<void>
  // Clear offline queue
  clearOfflineQueue: () => Promise<void>
}

/**
 * Custom hook for managing iOS ActivityTracker views
 * Provides a simple interface to show, hide, and check ActivityTracker visibility
 */
export const useActivityTracker = (): UseActivityTrackerReturn => {
  // GPS route recording is only allowed when the user's company has location tracking enabled.
  const userCompanies = useSelector(getUserCompanies)

  const [isVisible, setIsVisible] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [confirmationData, setConfirmationData] = useState<WorkoutConfirmationData | null>(null)
  const [isConfirmationRequired, setIsConfirmationRequired] = useState(false)
  const [uploadStats, setUploadStats] = useState<UploadStats>({
    queuedCount: 0,
    isOnline: true,
    isProcessing: false,
  })

  // Note: Event handling is now done by the global service to avoid duplication

  // Retry offline uploads manually
  const retryOfflineUploads = useCallback(async () => {
    setIsLoading(true)
    try {
      await activityUploadService.processQueue()
      setUploadStats(activityUploadService.getStats())
    } catch (err) {
      setError('Failed to retry uploads')
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Clear offline queue
  const clearOfflineQueue = useCallback(async () => {
    try {
      await activityUploadService.clearQueue()
      setUploadStats(activityUploadService.getStats())
      setSuccessMessage('Offline queue cleared')
    } catch (err) {
      setError('Failed to clear queue')
    }
  }, [])

  // Subscribe to global activity tracker service instead of setting up local listeners
  useEffect(() => {
    console.log('🎯 useActivityTracker: Subscribing to global service')

    // Subscribe to global service updates
    const unsubscribe = globalActivityTrackerService.subscribe((globalState) => {
      console.log('🔧 useActivityTracker: Received global state update:', globalState)
      setIsVisible(globalState.isVisible)
      setIsLoading(globalState.isLoading)
      setError(globalState.error)
      setSuccessMessage(globalState.successMessage)
      setConfirmationData(globalState.confirmationData)
      setIsConfirmationRequired(globalState.isConfirmationRequired)
      setUploadStats(globalState.uploadStats)
    })

    // Get initial state from global service
    const initialState = globalActivityTrackerService.getState()
    setIsVisible(initialState.isVisible)
    setIsLoading(initialState.isLoading)
    setError(initialState.error)
    setSuccessMessage(initialState.successMessage)
    setConfirmationData(initialState.confirmationData)
    setIsConfirmationRequired(initialState.isConfirmationRequired)
    setUploadStats(initialState.uploadStats)

    // Cleanup subscription on unmount
    return () => {
      console.log('🧹 useActivityTracker: Unsubscribing from global service')
      unsubscribe()
    }
  }, [])

  // Show the workout tracker interface
  const showWorkoutTracker = useCallback(async (): Promise<void> => {
    setIsLoading(true)
    setError(null)

    try {
      const result = await ActivityTracker.showWorkoutTracker()

      if (result.success) {
        setIsVisible(true)
      } else {
        setError('Failed to show workout tracker')
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred'
      setError(errorMessage)
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Start a workout with a specific activity type
  const startWorkout = useCallback(
    async (activityType: WorkoutType): Promise<void> => {
      setIsLoading(true)
      setError(null)

      // Only allow native GPS tracking when the company permits location tracking.
      const trackLocation = hasFeatureFlag(userCompanies, 'locationTracking')

      try {
        const result = await ActivityTracker.startWorkout({ activityType, trackLocation })

        if (result.success) {
          setIsVisible(true)
          console.log(`🏃‍♂️ Started ${result.activityType} workout (trackLocation: ${trackLocation})`)
        } else {
          setError('Failed to start workout')
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred'
        setError(errorMessage)
        throw err
      } finally {
        setIsLoading(false)
      }
    },
    [userCompanies],
  )

  // Hide the currently displayed ActivityTracker
  const hideActivityTracker = useCallback(async (): Promise<void> => {
    setIsLoading(true)
    setError(null)

    try {
      await ActivityTracker.hide()
      setIsVisible(false)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to hide ActivityTracker'
      setError(errorMessage)
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Check if ActivityTracker is currently visible
  const isActivityTrackerVisible = useCallback(async (): Promise<boolean> => {
    try {
      const result = await ActivityTracker.isVisible()
      setIsVisible(result.visible)
      return result.visible
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to check ActivityTracker visibility'
      setError(errorMessage)
      return false
    }
  }, [])

  // Track if we're already confirming to prevent duplicate calls
  const isConfirmingRef = useRef(false)

  // Confirm and save a workout with updated data
  const confirmWorkout = useCallback(
    async (title: string, type: WorkoutType): Promise<void> => {
      if (!isConfirmationRequired || !confirmationData) {
        console.error('❌ No workout confirmation required')
        return
      }

      // Prevent duplicate confirmation calls
      if (isConfirmingRef.current) {
        console.warn('⚠️ Workout confirmation already in progress, ignoring duplicate call')
        return
      }

      // Mark as confirming
      isConfirmingRef.current = true
      setIsLoading(true)
      setError(null)

      try {
        console.log('✅ Confirming workout with:', { title, type })
        const result = await ActivityTracker.confirmWorkout({ title, type })

        if (result.success) {
          console.log('✅ Workout confirmed successfully')
          setIsConfirmationRequired(false)
          setConfirmationData(null)
          // The Swift side will handle saving and emit workoutCompleted event
        } else {
          throw new Error('Failed to confirm workout')
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to confirm workout'
        console.error('❌ Error confirming workout:', errorMessage)
        setError(errorMessage)
      } finally {
        setIsLoading(false)
        // Reset confirming flag
        isConfirmingRef.current = false
      }
    },
    [isConfirmationRequired, confirmationData],
  )

  // Discard the current workout
  const discardWorkout = useCallback(async (): Promise<void> => {
    if (!isConfirmationRequired) {
      console.error('❌ No workout to discard')
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      console.log('🗑️ Discarding workout')
      const result = await ActivityTracker.discardWorkout()

      if (result.success) {
        console.log('✅ Workout discarded successfully')
        setIsConfirmationRequired(false)
        setConfirmationData(null)
        setSuccessMessage('Workout discarded')
      } else {
        throw new Error('Failed to discard workout')
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to discard workout'
      console.error('❌ Error discarding workout:', errorMessage)
      setError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }, [isConfirmationRequired])

  return {
    showWorkoutTracker,
    startWorkout,
    hideActivityTracker,
    isActivityTrackerVisible,
    confirmWorkout,
    discardWorkout,
    isVisible,
    isLoading,
    error,
    successMessage,
    uploadStats,
    confirmationData,
    isConfirmationRequired,
    retryOfflineUploads,
    clearOfflineQueue,
  }
}
